<template>
  <div
    class="flex h-[calc(100%-53px)] flex-col space-y-3 overflow-y-auto pt-6 pb-2"
  >
    <loader :is-loading="loading" />
    <div class="header-row">
      <div class="left">{{ $t("COMMON.TEMPLATE") }}:</div>
      <div class="right">
        <tooltip
          ref="templatesTooltip"
          placement="bottom-start"
          trigger="click"
          hide-on-click="toggle"
          :on-click-outside="hideDropdown"
          :append-to="body"
          :arrow="false"
          sticky="reference"
          theme="none"
          interactive
          :popper-options="popperOptions"
        >
          <template #default>
            <lf-pill
              size="sm"
              class="ml-2 h-[26px] cursor-pointer font-bold uppercase"
              color-index="15"
            >
              <div class="flex items-center space-x-2">
                <div data-cy="template-name" class="w-30 truncate text-left">
                  <span>{{ selectedTemplate.name }}</span>
                </div>
                <icon-base
                  :icon="IconArrowUp"
                  height="10"
                  width="10"
                  :class="{
                    '-translate-y-1 rotate-180':
                      !templatesTooltip?.state.isVisible,
                  }"
                />
              </div>
            </lf-pill>
          </template>
          <template #content>
            <div class="w-100 rounded-md bg-white p-2 shadow-lg">
              <div class="sticky pb-2">
                <search-input
                  v-model="search"
                  data-cy="sms-template-search"
                  class="h-10 w-full"
                  no-padding
                  no-margin
                />
              </div>
              <ul
                data-cy="sms-templates"
                class="h-auto max-h-100 overflow-x-hidden overflow-y-auto px-1 text-sm text-headline"
              >
                <li
                  v-for="template in filteredTemplatesOptions"
                  :key="template.id"
                  ref="templateListRef"
                  v-tooltipable
                  class="cursor-pointer truncate rounded-sm px-3 py-2 text-xs font-semibold whitespace-nowrap hover:bg-gray-200"
                  @click="selectTemplate(template)"
                >
                  <span :tooltip="template.name">{{ template.name }}</span>
                </li>
              </ul>
            </div>
          </template>
        </tooltip>
      </div>
    </div>
    <div class="header-row">
      <div class="left">
        {{ $t("SUBMISSIONS.SEND_TO") }}
      </div>
      <div class="right">
        <div class="w-full">
          <multiselect-dropdown
            class="multiselect-dropdown w-full"
            name="to"
            placeholder=""
            :value="selectedRecipients"
            :options="recipientOptions"
            is-absolute-positioned
            scrollable-parent="do-service"
            :allow-trim="false"
            @change="handleUpdateRecipients"
          />
        </div>
      </div>
    </div>
    <div class="header-row">
      <div class="left">{{ $t("COMMON.FROM") }}:</div>
      <div class="right">
        <sms-from-number />
      </div>
    </div>
    <div class="grow">
      <dynamic-field
        name="smsText"
        class="sms-headless"
        :placeholder="$t('ACTIVITY.SMS.MESSAGE_PLACEHOLDER')"
        :model-value="selectedTemplate?.body || ''"
        :type="CommunicationType.sms"
        has-length-counter
        :max-length="MAX_SMS_MESSAGE_LENGTH"
        :reply-to-message="replyToMessage"
        show-toolbar
        toolbar-type="simple"
        @clean-content:update="handleCleanContentUpdate"
        @update:model-value="selectedTemplate.body = $event"
        @submit="sendSms"
      />
      <div
        ref="sendButtonBlockRef"
        class="mx-2 flex items-center rounded-b border border-t-0 bg-disabled p-2"
        :class="isMessageTooLong ? 'justify-between' : 'justify-end'"
      >
        <span v-if="isMessageTooLong" class="text-error">
          {{ $t("ACTIVITY.SMS.TOO_LONG_MESSAGE") }}
        </span>
        <div class="flex items-center space-x-4">
          <span
            v-if="!!savedPhoneNumber"
            class="font-semibold uppercase"
            data-cy="from-number-display"
          >
            {{ savedPhoneDisplay }}
          </span>
          <primary-button
            data-cy="send-button-sms"
            class="send-button"
            :disabled="isSendingDisabled"
            @click="sendSms"
          >
            <icon-base :icon="IconSend" class="text-white" />
          </primary-button>
        </div>
      </div>
      <primary-button
        data-cy="send-button-2"
        class="send-button"
        :disabled="isSendingDisabled"
        :class="isSendButtonBlockVisible ? 'hidden' : 'sticky right-3 bottom-0'"
        @click="sendSms"
      >
        <icon-base :icon="IconSend" class="text-white" />
      </primary-button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useElementVisibility } from "@vueuse/core";
import { computed, ref, toRaw } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";
import { v4 as uuid } from "uuid";

import { CommunicationType } from "@/enums/communicationLogs";
import { stripHTMLTagsFromString } from "@/helpers/common";
import { MAX_SMS_MESSAGE_LENGTH } from "@/helpers/constants/communicationTemplates";
import { zeroWidthSpaceCharRegex } from "@/helpers/textEditor";
import { usePromiseWrapper } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import communicationService from "@/services/modules/communication";
import useSmsStore from "@/stores/sms";

import DynamicField from "@/components/DynamicField.vue";
import SmsFromNumber from "@/components/communicationLogs/communication/sms/SmsFromNumber.vue";
import IconArrowUp from "@/components/icons/IconArrowUp.vue";
import IconSend from "@/components/icons/IconSend.vue";
import IconBase from "@/components/ui/IconBase.vue";
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";
import MultiselectDropdown from "@/components/ui/inputs/MultiselectDropdown.vue";

import type { SmsTemplate } from "@/models/clients";
import type { SendSmsPayload } from "@/models/communication";
import type { MultiSelectOption } from "@/models/options";
import type { TippyComponent } from "vue-tippy";

const { replyToMessage = "" } = defineProps<{
  replyToMessage?: string;
}>();

const emit = defineEmits(["close-dialog"]);

type SendSmsTemplate = Pick<SmsTemplate, "name"> & {
  id?: string;
  body?: string;
};

const store = useSmsStore();
const { t } = useI18n();
const { savedPhoneNumber, savedPhoneDisplay } = useTwilio();

const { modalData } = storeToRefs(store);
modalData.value.templates.unshift({
  id: "0",
  name: t("COMMON.SELECT"),
  body: "",
});
const selectedTemplate = ref<SendSmsTemplate>(
  structuredClone(
    toRaw(modalData.value?.templates?.[0]) || {
      name: "",
      body: "",
    }
  )
);
const sendButtonBlockRef = ref<HTMLElement | null>(null);
const isSendButtonBlockVisible = useElementVisibility(sendButtonBlockRef);

const isMessageTooLong = ref(false);
const search = ref("");
const body = ref(document.body);
const templateListRef = ref<(HTMLElement | null)[]>([]);
const templatesTooltip = ref<TippyComponent | null>(null);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["top-start"] },
    },
  ],
});

const ownersWithApplications = computed(() =>
  modalData.value.applications.map(({ owners, id }) => ({ owners, id })).flat()
);

const recipientOptions = computed(() => {
  return ownersWithApplications.value.reduce(
    (
      result: (MultiSelectOption & {
        applicationId: string;
        telephone: string;
        isPrimary?: boolean;
      })[],
      { owners, id: applicationId }
    ) => {
      owners.forEach((owner) => {
        const label = owner.is_primary ? `(${t("COMMON.PRIMARY")})` : "";
        result.push({
          id: uuid(),
          name: `${owner.full_name} ${owner.telephone} ${label}`.trim(),
          telephone: owner.telephone,
          isPrimary: owner.is_primary,
          applicationId,
        });
        owner.additional_telephones.forEach((telephone) => {
          result.push({
            id: uuid(),
            name: `${owner.full_name} ${telephone}`,
            telephone,
            applicationId,
          });
        });
      });
      return result;
    },
    []
  );
});

const filteredTemplatesOptions = computed(() => {
  return modalData.value?.templates.filter(
    (field) =>
      field.name?.toLowerCase().includes(search.value.toLowerCase()) &&
      field.name !== t("COMMON.SELECT")
  );
});

const selectedRecipients = ref<Pick<MultiSelectOption, "id">[]>(
  recipientOptions.value.reduce(
    (result: Pick<MultiSelectOption, "id">[], { id, isPrimary }) => {
      if (isPrimary) {
        result.push({ id });
      }
      return result;
    },
    []
  )
);

const isSendingDisabled = computed(
  () =>
    !selectedTemplate.value.body ||
    !selectedRecipients.value.length ||
    isMessageTooLong.value
);

const handleUpdateRecipients = (value: Pick<MultiSelectOption, "id">[]) => {
  selectedRecipients.value = value;
};

const handleCleanContentUpdate = (cleanContent: string) => {
  selectedTemplate.value.body = cleanContent;
  isMessageTooLong.value =
    Math.ceil(cleanContent.length / MAX_SMS_MESSAGE_LENGTH) > 10;
};

const { fetchWrapper: sendSms, loading } = usePromiseWrapper(async () => {
  if (isSendingDisabled.value) {
    return;
  }

  const applicationsPayload = selectedRecipients.value.reduce(
    (result: Record<string, string[]>, curr) => {
      const recipientOption = recipientOptions.value.find(
        (option) => option.id === curr.id
      );
      if (!recipientOption) {
        return result;
      }
      const { applicationId, telephone } = recipientOption;
      if (!result[applicationId]) {
        result[applicationId] = [];
      }
      result[applicationId].push(telephone);
      return result;
    },
    {}
  );

  const payload: SendSmsPayload = {
    applications: applicationsPayload,
    message: stripHTMLTagsFromString(
      selectedTemplate.value.body || "",
      false,
      true
    )
      .replace(zeroWidthSpaceCharRegex, "")
      .trim(),
  };
  const from = savedPhoneNumber.value?.phone_number;
  if (from) {
    payload.from = from;
  }
  await communicationService.sendSms(payload);
  emit("close-dialog");
});

const selectTemplate = (template: SendSmsTemplate) => {
  selectedTemplate.value = structuredClone(toRaw(template));
  hideDropdown();
};

const hideDropdown = () => {
  templatesTooltip.value?.hide();
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.header-row {
  @apply flex w-full items-start space-x-2;

  .left {
    @apply w-1/5 pl-4 font-semibold text-label;
  }

  .right {
    @apply relative flex w-4/5 justify-start pr-4;
  }
}
.pill-item {
  @apply flex items-center space-x-2 rounded-full bg-disabled/50 p-2 pr-1 text-xs text-headline;
}
.remove-button {
  @apply inline-flex h-5 w-5 items-center justify-center rounded-full bg-disabled-medium text-white;
}

.send-button {
  @apply ml-auto w-[34px] px-2;
}

:deep() {
  .multiselect-dropdown {
    @apply mb-0;
  }

  .multiselect-dropdown .dropdown-options {
    @apply max-h-30;
  }

  .send-button[disabled] {
    @apply bg-disabled-medium;
  }

  .ql-formats {
    display: none;
    &:nth-child(4) {
      display: inline-block;
    }
  }
}
</style>
